import { useState } from 'react'
import { Trans, t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Alert, MenuItem, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'

import DateTimePickerInput from '@/components/ui/inputs/DateTimePickerInput'
import { useCreateTimeTrackingSessionMutation } from '@/core/api'
import { TimeTrackingAdminStaff } from '@/core/api/time-tracking'

import { REASON_CODE_VALUES, getReasonCodeOptions } from '../lib/reasonCodes'

const schema = yup.object({
  staff: yup.string().required(),
  startTime: yup.mixed<dayjs.Dayjs>().required(),
  endTime: yup.mixed<dayjs.Dayjs>().required(),
  reasonCode: yup.string().oneOf(REASON_CODE_VALUES).required(),
  comment: yup.string().optional(),
})

type FormData = yup.InferType<typeof schema>

type AdminCreateSessionDialogProps = {
  open: boolean
  staffs: TimeTrackingAdminStaff[]
  onClose: () => void
}

export function AdminCreateSessionDialog({ open, staffs, onClose }: AdminCreateSessionDialogProps) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()
  const reasonCodeOptions = getReasonCodeOptions()

  const [createSession, { isLoading }] = useCreateTimeTrackingSessionMutation()
  const [apiError, setApiError] = useState<string | null>(null)

  const { control, handleSubmit, register, reset } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
  })

  const onSubmit = async (data: FormData) => {
    setApiError(null)
    try {
      await createSession({
        staff: data.staff,
        startTime: data.startTime!.toISOString(),
        endTime: data.endTime!.toISOString(),
        reasonCode: data.reasonCode as any,
        comment: data.comment,
      }).unwrap()

      enqueueSnackbar(t`Session created successfully`, { variant: 'success' })
      reset()
      onClose()
    } catch (err: any) {
      const message = err?.data?.message || err?.message || t`Failed to create session`
      setApiError(typeof message === 'string' ? message : t`Failed to create session`)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Trans>Add session</Trans>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            {apiError && <Alert severity="error">{apiError}</Alert>}
            <Controller
              name="staff"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t`Staff`} fullWidth>
                  {staffs.map((staff) => (
                    <MenuItem key={staff._id} value={staff._id}>
                      {staff.userInfo.name} {staff.userInfo.surname}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="startTime"
              control={control}
              render={({ field }) => <DateTimePickerInput label={t`Start time`} value={field.value ?? null} onChange={(val) => field.onChange(val)} fullWidth />}
            />
            <Controller
              name="endTime"
              control={control}
              render={({ field }) => <DateTimePickerInput label={t`End time`} value={field.value ?? null} onChange={(val) => field.onChange(val)} fullWidth />}
            />
            <Controller
              name="reasonCode"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t`Reason`} fullWidth>
                  {reasonCodeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <TextField {...register('comment')} label={t`Comment`} multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            <Trans>Create</Trans>
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
